/**
 * Easy Cart Update Functions
 *
 * Simple functions to update cart content using existing theme infrastructure
 */

/**
 * Easy cart refresh - Uses existing cart update system
 * @param {string} source - Source identifier for the update
 * @returns {Promise<boolean>} - Success status
 */
async function updateCartContent(source = "manual") {
  try {
    console.log("MrBeast | Easy Cart Update - Refreshing cart content");

    // Fetch fresh cart data
    const response = await fetch("/cart.js", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch cart data");
    }

    const cartData = await response.json();

    // Use existing PubSub system
    if (
      typeof publish === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined"
    ) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: source,
        cartData: cartData,
      });
    }

    // Dispatch custom event for broader compatibility
    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: cartData,
        bubbles: true,
      }),
    );

    console.log("MrBeast | Easy Cart Update - Success");
    return true;
  } catch (error) {
    console.error("MrBeast | Easy Cart Update - Error:", error);
    return false;
  }
}

/**
 * Update cart drawer with sections
 * @param {Object} cartData - Cart data object
 * @returns {Promise<boolean>} - Success status
 */
async function updateCartDrawer(cartData = null) {
  try {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) {
      console.warn("Cart drawer not found");
      return false;
    }

    // Get fresh cart data if not provided
    if (!cartData) {
      const response = await fetch("/cart.js");
      cartData = await response.json();
    }

    // Get sections that need updating
    const sectionsToRender = cartDrawer.getSectionsToRender();

    if (sectionsToRender && typeof cartDrawer.renderContents === "function") {
      // For now, use the simple reload approach for reliability
      location.reload();
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error updating cart drawer:", error);
    return false;
  }
}

/**
 * Simple cart refresh - Just triggers existing events
 */
function refreshCart() {
  updateCartContent("refresh");
  console.log("MrBeast | Easy Cart Update - Refreshed cart");
}

/**
 * Add item to cart and refresh
 * @param {string|number} variantId - Product variant ID
 * @param {number} quantity - Quantity to add (default: 1)
 * @returns {Promise<boolean>} - Success status
 */
async function addToCartAndRefresh(variantId, quantity = 1) {
  try {
    // Add to cart
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items: [{ quantity: quantity, id: variantId }],
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to add to cart");
    }

    // Refresh cart content
    await updateCartContent("add-to-cart");
    return true;
  } catch (error) {
    console.error("Error adding to cart:", error);
    return false;
  }
}

/**
 * Update item quantity in cart
 * @param {number} line - Line number in cart (1-based)
 * @param {number} quantity - New quantity
 * @returns {Promise<boolean>} - Success status
 */
async function updateCartItemQuantity(line, quantity) {
  try {
    const body = JSON.stringify({
      line: line,
      quantity: quantity,
    });

    const response = await fetch(window.routes.cart_change_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: body,
    });

    if (!response.ok) {
      throw new Error("Failed to update quantity");
    }

    // Refresh cart content
    await updateCartContent("quantity-update");
    return true;
  } catch (error) {
    console.error("Error updating quantity:", error);
    return false;
  }
}

// Expose functions globally for easy access
window.updateCartContent = updateCartContent;
window.updateCartDrawer = updateCartDrawer;
window.refreshCart = refreshCart;
window.addToCartAndRefresh = addToCartAndRefresh;
window.updateCartItemQuantity = updateCartItemQuantity;

// console.log("MrBeast | Easy Cart Update - Functions loaded");
